import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const GaumukhTapovan = () => {
  const tourData = {
    name: "Gaumukh Tapovan Trek",
    duration: "05 Nights 06 Days",
    price: "₹ 11,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935180/jia7peu1ixqjpom8thm8.webp",
    altitude: "14,600 ft",
    difficulty: "Difficult",
    region: "Uttarakhand",
    highlights: ["Gangotri glacier", "Sacred site", "High altitude"],
    bestTime: "May to October",
    description: "The Gaumukh Tapovan Trek is a challenging and spiritually significant trek in Uttarakhand that takes you to the source of the Ganga River. This difficult trek offers breathtaking views of the Gangotri glacier and is considered one of the most sacred treks in the Himalayas."
  };

  return (
    <>
      <Helmet>
        <title>Gaumukh Tapovan Trek - NomadTrailz</title>
        <meta name="description" content="Experience the challenging Gaumukh Tapovan Trek in Uttarakhand. Sacred trek to the source of Ganga River with stunning glacier views." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Sacred trek to the source of Ganga River with stunning glacier views
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you to the sacred source of the Ganga River at Gaumukh, 
                    where the glacier meets the river. The journey passes through the spiritual 
                    town of Gangotri and offers stunning views of the Gangotri glacier and 
                    surrounding peaks. It's a challenging trek that requires good physical 
                    fitness and mental preparation.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Sacred source of Ganga River</li>
                    <li>Gangotri glacier views</li>
                    <li>High altitude adventure</li>
                    <li>Spiritual significance</li>
                    <li>Challenging trek</li>
                    <li>Mountain photography</li>
                    <li>Alpine meadows</li>
                    <li>Bhagirathi range views</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: From Rishikesh to Gangotri</h4>
                    <ul>
                      <li>Your trek to Gaumukh Tapovan will begin from Rishikesh, where you can take a bus or train to get there.</li>
                      <li>Your trip to Gangotri will start in Rishikesh and take you via Landour, Moriana Top, Uttarkashi, and Harsil, among other notable sites.</li>
                      <li>While in Uttarkashi, be sure to get everything you need. It is advisable to withdraw the necessary funds from this town since it is the final one with many ATM services.</li>
                      <li>In the town of Gangotri, you may immerse yourself in the spiritual values. You may learn about the local Hindu faith and its importance at the Ganga Maiya Temple.</li>
                      <li>Besides that, Gangotri is a great place to stay because of all the hotels, restaurants, and stores.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Gangotri to Bhojbasa</h4>
                    <ul>
                      <li>Our journey to Bhojbasa today will be a 14-kilometer walk. We will arrive at Gangotri National Park after the first 2km.</li>
                      <li>Here we will have our permissions to enter the Gangotri National Park protected area verified.</li>
                      <li>A couple stream crossings punctuate the flat terrain that follows for the following 5km. The landscape is stunning.</li>
                      <li>You may view Mount Sudarshan and the Ganga River flowing to your right from this vantage point.</li>
                      <li>We will arrive at Chirbasa after navigating a few challenging sections, including boulder jumping in the following 2km. Here is a great spot to enjoy some tea or maybe a packed lunch.</li>
                      <li>Bhojbasa is about 5 km away, after we pass through a few areas of scree and rock falls. The majestic Bhagirathi range of peaks, so called in honour of King Bhagirathi, is a sight to see upon arrival at Bhojbasa.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Bhojbasa to Gaumukh Tapovan</h4>
                    <ul>
                      <li>From Bhojbasa, we would have to hike 9 km to reach Tapovan. At Bhojbasa, we shall cross the Ganga River using a tram vehicle that is run manually.</li>
                      <li>We will continue our walk from the other side, mostly on level terrain, until we reach Gaumukh, the 4km source of the Ganga.</li>
                      <li>After this point, the route starts to ascend steeply, culminating in a challenging final km.</li>
                      <li>Finally, we arrive at Tapovan, a picturesque alpine meadow that serves as both the starting point for hikes up Mount Shivling and the dwelling place of several wise men and women who have found solace in contemplation here.</li>
                      <li>This will be our campsite for the day.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Tapovan to Bhojbasa</h4>
                    <ul>
                      <li>By way of Gomukh, make your way down to Bhojbasa after breakfast.</li>
                      <li>As the sun rises, take in the breathtaking views of the towering mountains and the scenic landscape, and then say your goodbyes to them.</li>
                      <li>Retrace your steps, you will arrive at Bhojbasa after stopping at Gomukh.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: From Bhojbasa to Gangotri</h4>
                    <ul>
                      <li>Get up at an early hour. Proceed to Gangotri from Bhojbasa and descend.</li>
                      <li>Take pleasure in the last glimmers of Mount Shivling as you make your way down through the rugged terrain.</li>
                      <li>When you arrive at the Gangotri hotel, make your way to the Gangotri temple and see the Ganga Aarti taking place there.</li>
                      <li>Gangotri is the location where the arrangements will be made at a hotel.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: From Gangotri to Rishikesh</h4>
                    <ul>
                      <li>As you continue on your journey with us, this is the last breakfast you will have.</li>
                      <li>We will deliver the certificates, and then, after taking some stunning photographs, we will say our goodbyes to everyone who has participated in the Gangotri and Tapovan trek.</li>
                      <li>A picturesque trip from Gangotri to Rishikesh would get you between six and seven o'clock in the evening.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pick and Drop Transfers from/to Rishikesh</li>
                    <li>All meals are included with proper hygiene parameters</li>
                    <li>Triple/double sharing tent accommodations are available</li>
                    <li>During the trek, there will be morning and evening tea with a small refreshment</li>
                    <li>All tolls and forest fees</li>
                    <li>Trekking gears (tents, sleeping bags, ropes, gaiters, crampons etc.)</li>
                    <li>First Aid Medical Kit</li>
                    <li>Expert Guide</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Food during pick and drop transfers</li>
                    <li>Porters/mules charges</li>
                    <li>Personal expenses</li>
                    <li>Insurance</li>
                    <li>Anything apart from the inclusions</li>
                    <li>GST 5%</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>14,600 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>38 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Difficult</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Rishikesh)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 11,999 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 12,499 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 12,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <h4>Rishikesh</h4>
                  <ul>
                    <li><strong>ISBT Rishikesh:</strong> Between 6:00 AM to 7:00 AM</li>
                    <li><strong>Railway Station:</strong> Between 6:00 AM to 7:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Sacred source of Ganga River</li>
                    <li>Gangotri glacier views</li>
                    <li>High altitude adventure</li>
                    <li>Spiritual significance</li>
                    <li>Challenging trek</li>
                    <li>Mountain photography</li>
                    <li>Alpine meadows</li>
                    <li>Bhagirathi range views</li>
                    <li>Expert guide and support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default GaumukhTapovan;
